import { computed, onBeforeMount } from 'vue';
import { SMS_RESEND_TIME } from '@/constants';
import { differenceInSeconds, getTime } from 'date-fns';

type RemainingTime = {
  usersPhone: string;
  smsSentTime: number;
};

const countdownStorageKey = 'sms_countdown_times';

export const useCountdownStorage = (usersPhone: string) => {
  const secondsBetween = (timestamp1: number, timestamp2: number) => {
    return differenceInSeconds(new Date(timestamp2), new Date(timestamp1));
  };

  const calculateSecondsLeft = (remainingTime: number) => {
    const secondsLeftToSendAgain = SMS_RESEND_TIME - Math.abs(remainingTime);

    return secondsLeftToSendAgain < 0 ? 0 : secondsLeftToSendAgain;
  };

  const getCurrentTime = () => getTime(new Date());

  const allRemainingTimes = JSON.parse(
    localStorage.getItem(countdownStorageKey) || '[]'
  ) as RemainingTime[];

  const currentUserRemainingTimeRecord = computed(() => {
    return allRemainingTimes.find(record => record.usersPhone === usersPhone);
  });

  // clear all if latest is longer than sms retry time
  onBeforeMount(() => {
    const [latestCountdown] = allRemainingTimes;

    if (!latestCountdown) {
      return;
    }

    const remainingTimeForLatest = secondsBetween(
      getCurrentTime(),
      latestCountdown.smsSentTime
    );

    const secondsLeftToLatest = calculateSecondsLeft(remainingTimeForLatest);

    if (secondsLeftToLatest) {
      return;
    }

    localStorage.removeItem(countdownStorageKey);
  });

  const setNewTime = () => {
    allRemainingTimes.unshift({
      smsSentTime: getCurrentTime(),
      usersPhone,
    });

    localStorage.setItem(
      countdownStorageKey,
      JSON.stringify(allRemainingTimes)
    );
  };

  const getRemainingTime = () => {
    if (!currentUserRemainingTimeRecord.value) {
      return 0;
    }

    const remainingTime = secondsBetween(
      getCurrentTime(),
      currentUserRemainingTimeRecord.value!.smsSentTime
    );

    return calculateSecondsLeft(remainingTime);
  };

  return {
    getRemainingTime,
    setNewTime,
  };
};
